import { FC, useState } from 'react';
import guideImg from 'assets/return-flow-guide.jpg';
import styles from './returnFlowNotice.module.scss';

// 一天內只提醒一次。
//
// 原本用 sessionStorage,但訪客歸還會跳出瀏覽器到 LINE app 再回來 —— 那是新的 session,
// 旗標消失,使用者在同一輪歸還中被同一張教學圖擋第二次(同仁 2026-09-01 回報)。
// 改用 localStorage + 24 小時效期:跨 LINE 來回不會重跳,隔天新流程還是提醒得到。
const SEEN_KEY = 'pp_return_flow_notice_seen_at';
const SEEN_TTL_MS = 24 * 60 * 60 * 1000;

export const hasSeenReturnFlowNotice = (): boolean => {
  try {
    const at = Number(window.localStorage.getItem(SEEN_KEY) ?? 0);

    return at > 0 && Date.now() - at < SEEN_TTL_MS;
  } catch {
    return false;
  }
};

export const markReturnFlowNoticeSeen = (): void => {
  try {
    window.localStorage.setItem(SEEN_KEY, String(Date.now()));
  } catch {
    // 無痕模式等寫不進去就算了,大不了多跳一次
  }
};

/**
 * 歸還進行到一半(從 LINE 綁定流程回來、或 sessionStorage 還留著待處理的掃描)時,
 * 使用者要的是把這輪走完,不是再看一次教學 —— 這種情況一律不擋。
 */
export const isMidReturnFlow = (): boolean => {
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get('pendingRentLog') || p.get('fromGuest') === '1') return true;
  } catch {
    /* ignore */
  }
  try {
    // 與 return.tsx 的 PENDING_SCAN_KEY 同一支 key
    return !!window.sessionStorage.getItem('pending_return_scan');
  } catch {
    return false;
  }
};

interface ReturnFlowNoticeProps {
  visible: boolean;
  onClose: () => void;
  // 'notice' = 歸還前擋一次的更新通知;'guide' = 首頁「歸還流程」按鈕主動查看,
  // 同一張教學圖共用,只差標題與按鈕字樣(查看模式不該寫成「開始歸還」)。
  mode?: 'notice' | 'guide';
}

const ReturnFlowNotice: FC<ReturnFlowNoticeProps> = ({ visible, onClose, mode = 'notice' }) => {
  const [zoomed, setZoomed] = useState<boolean>(false);

  if (!visible) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-label="歸還流程更新通知">
      <div className={styles.panel}>
        <div className={styles.head}>
          {mode === 'notice' && <span className={styles.badge}>新流程上線</span>}
          <h2>{mode === 'notice' ? '歸還流程已更新' : '歸還流程'}</h2>
          <p className={styles.summary}>
            手機掃描包裝取碼 → FamiPort 機台印單 → 貼上包裝 → 交給店員歸還
          </p>
        </div>

        {/* 教學圖很長,用捲動區塊呈現;點圖可全螢幕放大細看 */}
        <div className={styles.imageScroll}>
          <button
            type="button"
            className={styles.imageBtn}
            onClick={() => setZoomed(true)}
          >
            <img src={guideImg} alt="循環包裝歸還教學" />
          </button>
        </div>
        <p className={styles.hint}>點圖片可放大閱讀</p>

        <button type="button" className={styles.okBtn} onClick={onClose}>
          {mode === 'notice' ? '我知道了，開始歸還' : '關閉'}
        </button>
      </div>

      {zoomed && (
        <div
          className={styles.lightbox}
          onClick={() => setZoomed(false)}
          role="dialog"
          aria-label="放大教學圖"
        >
          <button
            type="button"
            className={styles.lightboxClose}
            onClick={() => setZoomed(false)}
            aria-label="關閉"
          >
            ×
          </button>
          <img
            src={guideImg}
            alt="循環包裝歸還教學"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ReturnFlowNotice;
